import React, { useEffect, useState } from "react";
import MultiImageInput from "@/components/MultiImageInput";
import { MicRow, appendText } from "@/components/VoiceMic";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { vendorCreateService, vendorUpdateService } from "@/lib/vendorApi";
import { toast } from "sonner";
import ImageInput from "@/components/ImageInput";

// Mobile: keep the focused field above the on-screen keyboard.
const keepFieldVisible = (e) => {
  const t = e.target;
  if (window.innerWidth >= 640 || !/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.type === "checkbox") return;
  setTimeout(() => t.scrollIntoView({ block: "center", behavior: "smooth" }), 300);
};

const SUGGESTED_CATEGORIES = ["home-repair", "beauty", "cleaning", "education", "fitness", "cooking", "delivery", "photography", "events", "other"];

export default function AddServiceDialog({ open, onOpenChange, shops, service, onSaved }) {
  const { formatApiError } = useApp();
  const isEdit = !!service?.id;
  const [form, setForm] = useState(makeInitial(shops, service));
  const [saving, setSaving] = useState(false);

  // Sync form when opening or when service changes
  useEffect(() => {
    if (open) setForm(makeInitial(shops, service));
  }, [open, service?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_from: parseFloat(form.price_from) || 0,
        price: form.price === "" ? null : parseFloat(form.price),
        experience_years: form.experience_years === "" ? null : parseInt(form.experience_years, 10),
        service_pincodes: form.service_pincodes
          ? form.service_pincodes.split(",").map((p) => p.trim()).filter(Boolean)
          : null,
        shop_id: form.shop_id || shops[0]?.shop_id,
      };
      if (isEdit) await vendorUpdateService(service.id, payload);
      else await vendorCreateService(payload);
      toast.success(isEdit ? "Service updated" : "Service added");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] max-sm:max-h-[90dvh] overflow-y-auto overscroll-contain max-sm:w-[calc(100%-1rem)] max-sm:rounded-2xl max-sm:p-4 max-sm:pb-0" data-testid="add-service-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl max-sm:pr-8">{isEdit ? "Edit service" : "Add new service"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} onFocus={keepFieldVisible} className="space-y-3 min-w-0">
          {shops.length > 1 && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Shop</label>
              <select value={form.shop_id} onChange={(e) => set("shop_id", e.target.value)} disabled={isEdit} className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500 disabled:opacity-60" data-testid="new-service-shop">
                {shops.map((s) => <option key={s.shop_id} value={s.shop_id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Service name*</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} required className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-name" />
            <MicRow onText={(t) => set("name", appendText(form.name, t))} testid="mic-service-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Provider / display name</label>
            <input value={form.provider} onChange={(e) => set("provider", e.target.value.slice(0, 120))} placeholder="Shop name used if blank" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-provider" />
            <p className="text-[11px] text-slate-400 mt-1">Customers see this name with your service and in WhatsApp enquiries.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Qualification (optional)</label>
              <input value={form.qualification} onChange={(e) => set("qualification", e.target.value.slice(0, 200))} placeholder="e.g. BA LLB, ITI Electrician" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-qualification" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Specialties (optional)</label>
              <input value={form.specialties} onChange={(e) => set("specialties", e.target.value.slice(0, 300))} placeholder="e.g. Criminal cases, AC repair" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-specialties" />
            </div>
          </div>
          <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Category*</label>
              <input list="svc-cats" value={form.category} onChange={(e) => set("category", e.target.value)} required placeholder="e.g. home-repair" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-category" />
              <datalist id="svc-cats">
                {SUGGESTED_CATEGORIES.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Subcategory</label>
              <input value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} placeholder="Optional" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-subcategory" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Starting price (₹)*</label>
              <input value={form.price_from} onChange={(e) => set("price_from", e.target.value)} required type="number" min="0" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-price-from" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit</label>
              <input value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="Per visit / hour / session" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-unit" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Duration</label>
              <input value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="~ 30 min" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-duration" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Experience (yrs)</label>
              <input value={form.experience_years} onChange={(e) => set("experience_years", e.target.value)} type="number" min="0" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-experience" />
            </div>
          </div>
          <MultiImageInput value={form.images?.length ? form.images : form.image ? [form.image] : []} onChange={(l) => { set("images", l); set("image", l[0] || ""); }} captions={form.image_captions || {}} onCaptionsChange={(c) => set("image_captions", c)} label="Service photos (first = cover)" testid="new-service-image" disabled={saving} libraryCategory="home_services" />
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-description" />
            <MicRow onText={(t) => set("description", appendText(form.description, t))} testid="mic-service-description" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Service area pincodes</label>
            <input value={form.service_pincodes} onChange={(e) => set("service_pincodes", e.target.value)} placeholder="Comma separated, e.g. 110001, 110002" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-pincodes" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact phone</label>
            <input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value.replace(/\D/g, "").slice(0, 15))} inputMode="numeric" placeholder="10-digit mobile (shop number used if blank)" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid="new-service-phone" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 min-h-[40px]">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 sm:w-auto sm:h-auto" data-testid="new-service-active" />
            Active (visible to customers)
          </label>
          <div className="max-sm:sticky max-sm:bottom-0 max-sm:-mx-4 max-sm:px-4 max-sm:pt-2 max-sm:pb-4 max-sm:bg-white max-sm:border-t max-sm:border-slate-100">
            <button type="submit" disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl py-3 text-sm font-semibold" data-testid="new-service-submit">
              {saving ? "Saving..." : (isEdit ? "Save changes" : "Add service")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function makeInitial(shops, svc) {
  if (svc) {
    return {
      shop_id: svc.shop_id || shops[0]?.shop_id || "",
      name: svc.name || "",
      hindi: svc.hindi || "",
      category: svc.category || "",
      subcategory: svc.subcategory || "",
      price_from: svc.price_from ?? "",
      price: svc.price ?? "",
      unit: svc.unit || "",
      duration: svc.duration || "",
      experience_years: svc.experience_years ?? "",
      image: svc.image || "",
      images: svc.images?.length ? svc.images : svc.image ? [svc.image] : [],
      image_captions: svc.image_captions || {},
      description: svc.description || "",
      service_pincodes: Array.isArray(svc.service_pincodes) ? svc.service_pincodes.join(", ") : "",
      contact_phone: svc.contact_phone || "",
      provider: svc.provider || "",
      qualification: svc.qualification || "",
      specialties: svc.specialties || "",
      active: svc.active !== false,
    };
  }
  return {
    shop_id: shops[0]?.shop_id || "",
    name: "", hindi: "", category: "", subcategory: "",
    price_from: "", price: "", unit: "", duration: "", experience_years: "",
    image: "", description: "", service_pincodes: "", contact_phone: "", provider: "", qualification: "", specialties: "",
    active: true,
  };
}
